import type { ReactNode } from 'react';

/**
 * One section of a page, as its own box (round 3): a 1px border, a small
 * radius, and a heading bar in the brand colour (.panel and .panel-bar in
 * index.css). `span` places it on the 12-unit grid (.grid12): 12, 8, 6 or 4,
 * collapsing to 6 and then 12 on narrower screens. `note` sits at the bar's
 * right in ordinary case (a reference, a count). Actions go under the bar, in
 * the panel. `action`, one control, sits IN the bar on its right, drawn as the
 * Documents bar draws its "+" (BAR_ACTION, 44px; A-#234, A-#235); a panel that
 * passes none is drawn exactly as before.
 */
export function Panel({
  title, note, span = 12, rows2 = false, actions, action, children, id,
}: {
  title: ReactNode;
  note?: ReactNode;
  span?: 12 | 8 | 6 | 4;
  rows2?: boolean;
  actions?: ReactNode;
  /** one control in the bar, on its right */
  action?: ReactNode;
  children: ReactNode;
  id?: string;
}) {
  return (
    <section id={id} className={`panel span-${span}${rows2 ? ' rows-2' : ''}`}>
      {action === undefined ? (
        <h2 className="panel-bar">
          <span>{title}</span>
          {note !== undefined && <small>{note}</small>}
        </h2>
      ) : (
        <div className="panel-bar items-center">
          <h2 className="flex items-baseline gap-2">
            <span>{title}</span>
            {note !== undefined && <small>{note}</small>}
          </h2>
          <div className="text-base tracking-normal normal-case">{action}</div>
        </div>
      )}
      {actions && <div className="flex flex-wrap justify-end gap-2 px-3 pt-2">{actions}</div>}
      {children}
    </section>
  );
}

/** Puts a component that draws its own panel onto the grid. */
export function Cell({ span = 12, children }: { span?: 12 | 8 | 6 | 4; children: ReactNode }) {
  return <div className={`span-${span} min-w-0`}>{children}</div>;
}
